import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Search, SlidersHorizontal, X } from "lucide-react";

export default function RegistryFilters({ filters, setFilters, onSearch, total }) {
  const [states, setStates] = useState([]);
  const [crimeTypes, setCrimeTypes] = useState([]);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/meta/states"),
      api.get("/meta/crime-types"),
    ])
      .then(([s, c]) => {
        setStates(s.data);
        setCrimeTypes(c.data);
      })
      .catch(() => {});
  }, []);

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v, page: 1 }));
  const clearAll = () =>
    setFilters({ q: "", state: "", city: "", crime_type: "", year: "", page: 1 });

  const activeCount = ["q", "state", "city", "crime_type", "year"].filter(
    (k) => filters[k]
  ).length;

  return (
    <div className="border border-zinc-900 bg-white" data-testid="registry-filters">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSearch();
        }}
      >
        <div className="flex flex-col lg:flex-row">
          {/* Main search */}
          <div className="flex-1 flex items-center gap-3 p-4 border-b lg:border-b-0 lg:border-r border-zinc-900">
            <Search size={18} strokeWidth={2} className="text-zinc-900 flex-shrink-0" />
            <input
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })}
              placeholder="Search name, alias, city, or animal (e.g. horse, rooster)..."
              className="flex-1 bg-transparent outline-none text-base placeholder:text-zinc-400 font-medium"
              data-testid="registry-search-input"
            />
            {filters.q && (
              <button
                type="button"
                onClick={() => update("q", "")}
                className="text-zinc-400 hover:text-zinc-900"
                data-testid="registry-search-clear"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* State quick */}
          <div className="flex items-stretch border-b lg:border-b-0 lg:border-r border-zinc-900 relative">
            <select
              value={filters.state}
              onChange={(e) => update("state", e.target.value)}
              className="appearance-none pl-4 pr-10 py-4 bg-white text-zinc-900 text-sm font-semibold uppercase tracking-wider outline-none cursor-pointer border-0 focus:bg-zinc-50 [&>option]:bg-white [&>option]:text-zinc-900 [&>option]:normal-case"
              data-testid="registry-state-select"
            >
              <option value="">All States</option>
              {states.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.code} · {s.name}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 text-xs font-bold">▾</span>
          </div>

          <div className="flex">
            <button
              type="button"
              onClick={() => setExpanded((x) => !x)}
              className="px-5 py-4 border-r border-zinc-900 text-sm font-semibold uppercase tracking-wider flex items-center gap-2 hover:bg-zinc-100 transition-colors"
              data-testid="registry-filters-toggle"
            >
              <SlidersHorizontal size={14} />
              Filters
              {activeCount > 0 && (
                <span className="bg-red-600 text-white text-[10px] px-1.5 py-0.5 font-mono">
                  {activeCount}
                </span>
              )}
            </button>
            <button
              type="submit"
              className="px-6 py-4 bg-zinc-900 text-white text-sm font-semibold uppercase tracking-wider hover:bg-red-600 transition-colors"
              data-testid="registry-search-submit"
            >
              Search
            </button>
          </div>
        </div>

        {expanded && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-5 border-t border-zinc-300 bg-zinc-50">
            <div>
              <label className="label-eyebrow block mb-2">City</label>
              <input
                className="field"
                value={filters.city}
                onChange={(e) => update("city", e.target.value)}
                placeholder="e.g. Atlanta"
                data-testid="filter-city-input"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Crime Type</label>
              <select
                className="field"
                value={filters.crime_type}
                onChange={(e) => update("crime_type", e.target.value)}
                data-testid="filter-crime-select"
              >
                <option value="">Any</option>
                {crimeTypes.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Conviction Year</label>
              <input
                type="number"
                min="1990"
                max="2100"
                className="field"
                value={filters.year}
                onChange={(e) => update("year", e.target.value)}
                placeholder="e.g. 2024"
                data-testid="filter-year-input"
              />
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={clearAll}
                className="btn-outline w-full justify-center"
                data-testid="filter-clear-all"
              >
                Clear All
              </button>
            </div>
          </div>
        )}
      </form>
      {total !== undefined && (
        <div className="border-t border-zinc-300 px-5 py-2 flex items-center justify-between bg-zinc-50">
          <div className="label-eyebrow" data-testid="registry-result-count">
            {total} records · Sorted by conviction year (newest)
          </div>
          {activeCount > 0 && (
            <button
              onClick={clearAll}
              className="text-[11px] uppercase tracking-widest font-semibold text-red-600 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}
